import axios from "axios";
import React, {FC, ReactNode, useState} from "react";
import {Button, Icon, Modal, Radio} from "semantic-ui-react";
import City from "../../../domain/fundamentals/address/City";
import PostalCode from "../../../domain/fundamentals/address/PostalCode";
import Prefecture from "../../../domain/fundamentals/address/Prefecture";
import Street from "../../../domain/fundamentals/address/Street";

interface Props {
  postalCode: PostalCode;
  setPrefecture: (prefecture: Prefecture) => void;
  setCity: (city: City) => void;
  setStreet: (street: Street) => void;
}

const PostalCodeSearchButton: FC<Props> = (props: Props) => {
  const {postalCode, setPrefecture, setCity, setStreet} = props;
  const [open, setOpen] = useState(false);
  const [selectionAddresses, setSelectionAddresses] = useState<ReactNode>(null);
  const [selectAddress, setSelectAddress] = useState<{
    prefecture: Prefecture,
    city: City,
    street: Street,
  }>({
    prefecture: new Prefecture(),
    city: new City(),
    street: new Street(),
  });


  const search = (value: string) => {
    if (!value || value === "") return;

    // Google Map Geocoding API
    axios.get("https://maps.googleapis.com/maps/api/geocode/json", {
      params: {
        key: process.env.REACT_APP_GOOGLE_GEOCODING_KEY,
        language: "ja",
        address: value,
      }
    }).then(response => {
      if (response.status === 200) {
        if (response.data.results.length === 1) {
          let prefecture1: string = "";
          let prefecture2: string = "";
          let prefecture3: string = "";
          let prefecture4: string = "";
          let prefecture5: string = "";
          let city1: string = "";
          let city2: string = "";
          let street1: string = "";
          let street2: string = "";
          let street3: string = "";
          let street4: string = "";

          response.data.results.map(result => result.address_components.map(address_component => {
            if (address_component.types.find(value => value === "administrative_area_level_1")) {
              prefecture1 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "administrative_area_level_2")) {
              prefecture2 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "administrative_area_level_3")) {
              prefecture3 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "administrative_area_level_4")) {
              prefecture4 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "administrative_area_level_5")) {
              prefecture5 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "locality")) {
              city1 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "sublocality_level_1")) {
              city2 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "sublocality_level_2")) {
              street1 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "sublocality_level_3")) {
              street2 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "sublocality_level_4")) {
              street3 = `${address_component.long_name}`;
            }
            if (address_component.types.find(value => value === "sublocality_level_5")) {
              street4 = `${address_component.long_name}`;
            }
          }));

          setPrefecture(new Prefecture(`${prefecture1}${prefecture2}${prefecture3}${prefecture4}${prefecture5}`));
          setCity(new City(`${city1}${city2}`));
          setStreet(new Street(`${street1}${street2}${street3}${street4}`));
        } else {
          // 複数の住所が見つかった場合
          const results: {
            prefecture: Prefecture,
            city: City,
            street: Street,
          }[] = [];
          response.data.results.forEach(result => {
            let prefecture1: string = "";
            let prefecture2: string = "";
            let prefecture3: string = "";
            let prefecture4: string = "";
            let prefecture5: string = "";
            let city1: string = "";
            let city2: string = "";
            let street1: string = "";
            let street2: string = "";
            let street3: string = "";
            let street4: string = "";

            result.address_components.forEach(address_component => {
              if (address_component.types.find(value => value === "administrative_area_level_1")) {
                prefecture1 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "administrative_area_level_2")) {
                prefecture2 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "administrative_area_level_3")) {
                prefecture3 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "administrative_area_level_4")) {
                prefecture4 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "administrative_area_level_5")) {
                prefecture5 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "locality")) {
                city1 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "sublocality_level_1")) {
                city2 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "sublocality_level_2")) {
                street1 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "sublocality_level_3")) {
                street2 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "sublocality_level_4")) {
                street3 = `${address_component.long_name}`;
              }
              if (address_component.types.find(value => value === "sublocality_level_5")) {
                street4 = `${address_component.long_name}`;
              }
            });

            results.push({
              prefecture: new Prefecture(`${prefecture1}${prefecture2}${prefecture3}${prefecture4}${prefecture5}`),
              city: new City(`${city1}${city2}`),
              street: new Street(`${street1}${street2}${street3}${street4}`),
            });

            // ダイアログで選択してもらう
            const selectionAddresses = results.map(result => (
              <li style={{marginBottom: "0.5rem"}}>
                <Radio
                  name="addresses"
                  key="addresses"
                  label={`${result.prefecture.value}${result.city.value}${result.street.value}`}
                  onChange={() => setSelectAddress({
                    prefecture: result.prefecture,
                    city: result.city,
                    street: result.street,
                  })}
                />
              </li>
            ));
            setSelectionAddresses(
              <ul style={{listStyle: "none"}}>
                {selectionAddresses}
              </ul>
            );
            setOpen(true);
          });
        }
      }
    });
  };

  return (
    <>
      <Button
        icon
        labelPosition="left"
        onClick={() => search(postalCode.value)}
      >
        <Icon name="search"/>
        郵便番号検索
      </Button>
      <Modal
        onClose={() => setOpen(false)}
        open={open}
      >
        <Modal.Header>住所を選択してください</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <p>複数の住所が見つかりました。選択してください。</p>
          </Modal.Description>
          {selectionAddresses}
        </Modal.Content>
        <Modal.Actions>
          <Button positive onClick={() => {
            setPrefecture(selectAddress.prefecture);
            setCity(selectAddress.city);
            setStreet(selectAddress.street);
            setOpen(false);
          }}>決定</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default PostalCodeSearchButton;
